/**
 * KnowledgeTab Component
 * Manage agent knowledge base (RAG) - documents, spreadsheets, websites
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import {
  IconUpload,
  IconFile,
  IconFileSpreadsheet,
  IconWorld,
  IconTrash,
  IconRefresh,
  IconCheck,
  IconClock,
  IconX,
  IconBrandGoogleDrive,
  IconBrandNotion,
  IconSettings,
  IconInfoCircle,
  IconPlus,
  IconFileTypePdf,
  IconFileTypeDocx,
  IconFileTypeDoc,
  IconFileTypeXls,
  IconFileTypeCsv,
  IconFileTypeTxt,
  IconAlertCircle,
  IconBrain,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import AgentsFlowService from '../../services/AgentsFlowService';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { useAgentsFlowApi } from '../../hooks/useAgentsFlowApi';
import { UPLOAD_LIMITS } from '../../constants';
import TrainAgentModal from '../TrainAgentModal';
import ProBadge from '../ProBadge';
import useProFeature from '../../hooks/useProFeature';
import { useGooglePicker, DRIVE_RAG_MIME_TYPES } from '../../hooks/useGooglePicker';
import RAGGuideModal from '../RAGGuideModal';
import './KnowledgeTab.css';

const MAX_SOURCES_PER_TYPE = 5;
const MAX_TAGS_PER_DOC = 1;

// Normalização das tags — mantém em sync com backend `category_tags.normalize_tag`.
// Conversão: lowercase, remove acentos, espaços/hifens viram underscore.
const normalizeTag = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  // Remove combining marks (acentos decompostos pelo NFKD). Usamos a
  // Unicode property `\p{Mark}` em vez de range literal pra evitar
  // problemas com encoding/source-mangling em bundlers.
  let s = raw.normalize('NFKD').replace(/\p{Mark}/gu, '');
  s = s.trim().toLowerCase();
  // Espaços e hifens → underscore
  s = s.replace(/[\s\-]+/g, '_');
  // Mantém só [a-z0-9_]
  s = s.replace(/[^a-z0-9_]/g, '');
  // Colapsa underscores múltiplos + remove das pontas
  s = s.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  return s;
};

// ===============================================
// SOURCE COLORS & FILTERS
// ===============================================

const SOURCE_COLORS = {
  file:          { gradient: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', color: '#0284c7' },
  website:       { gradient: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', color: '#16a34a' },
  notion:        { gradient: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', color: '#1e293b' },
  google_drive:  { gradient: 'linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)', color: '#ca8a04' },
  google_sheets: { gradient: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', color: '#16a34a' },
};

const FILTERS = [
  { key: 'documents', label: 'Documentos', icon: IconFile, types: ['file'] },
  { key: 'websites', label: 'Websites', icon: IconWorld, types: ['website'] },
  // { key: 'notion', label: 'Notion', icon: IconBrandNotion, types: ['notion'] }, DESATIVADO POR ENQUANTO
  { key: 'drive', label: 'Google Drive', icon: IconBrandGoogleDrive, types: ['google_drive', 'google_sheets'] },
];

// ===============================================
// FILE TYPE ICONS
// ===============================================

const getFileIcon = (fileType) => {
  if (fileType?.includes('spreadsheet') || fileType?.includes('csv') || fileType?.includes('xlsx')) {
    return IconFileSpreadsheet;
  }
  return IconFile;
};

const getSourceIcon = (source) => {
  switch (source.source_type) {
    case 'website':
      return IconWorld;
    case 'google_sheets':
    case 'google_drive':
      return IconBrandGoogleDrive;
    case 'notion':
      return IconBrandNotion;
    default:
      return getFileIcon(source.file_type);
  }
};

// ===============================================
// EDIT TAGS MODAL
// ===============================================

const EditTagsModal = ({ show, onHide, source, suggestions, onSave }) => {
  const [tags, setTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setTags(Array.isArray(source?.categories) ? [...source.categories] : []);
      setInputValue('');
    }
  }, [show, source]);

  const filteredSuggestions = suggestions
    .filter((s) => !tags.includes(s) && s.includes(normalizeTag(inputValue)))
    .slice(0, 5);

  const addTag = (raw) => {
    const norm = normalizeTag(raw);
    if (!norm) return;
    if (norm.startsWith('__')) return;
    if (tags.includes(norm)) return;
    if (tags.length >= MAX_TAGS_PER_DOC) {
      Swal.fire({ icon: 'warning', title: 'Limite', text: `Máximo ${MAX_TAGS_PER_DOC} tags por documento.` });
      return;
    }
    setTags([...tags, norm]);
    setInputValue('');
  };

  const removeTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSave = async () => {
    // Comita texto pendente no input antes de salvar — evita perder tag
    // digitada sem Enter (bug reportado: usuário digita "Cardiologia" e
    // clica Salvar direto, esperando que vire chip).
    let finalTags = tags;
    const pending = inputValue.trim();
    if (pending) {
      const norm = normalizeTag(pending);
      if (norm && !norm.startsWith('__') && !tags.includes(norm) && tags.length < MAX_TAGS_PER_DOC) {
        finalTags = [...tags, norm];
        setTags(finalTags);
        setInputValue('');
      }
    }
    setSaving(true);
    try {
      await onSave(finalTags);
      onHide();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e?.response?.data?.error || 'Não foi possível salvar.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="kb-modal">
      <div className="kb-modal__header">
        <button className="kb-modal__close" onClick={onHide}>
          <IconX size={18} />
        </button>
        <h2 className="kb-modal__title" style={{ fontSize: 18 }}>Categorias do documento</h2>
        <p className="kb-modal__subtitle" style={{ fontSize: 13 }}>
          Tags escopam o documento a nós FAQ específicos. <strong>Deixe vazio</strong> se o documento se aplica a TODO o agente.
        </p>
      </div>
      <div className="kb-modal__body">
        <Form.Group>
          <Form.Label>Tags (max {MAX_TAGS_PER_DOC})</Form.Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {tags.map((t) => (
              <span key={t} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: '#e0f2fe', color: '#0369a1', padding: '4px 10px',
                borderRadius: 12, fontSize: 13, fontWeight: 500,
              }}>
                {t}
                <button
                  onClick={() => removeTag(t)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
                >
                  <IconX size={12} />
                </button>
              </span>
            ))}
          </div>
          {tags.length < MAX_TAGS_PER_DOC && (
            <>
              <Form.Control
                type="text"
                placeholder="Digite uma tag e Enter (ex: cardiologia)"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag(inputValue);
                  }
                }}
              />
              {inputValue && filteredSuggestions.length > 0 && (
                <div style={{
                  marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4,
                }}>
                  <span style={{ fontSize: 12, color: '#666', marginRight: 4 }}>Sugestões:</span>
                  {filteredSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => addTag(s)}
                      style={{
                        background: '#f3f4f6', border: '1px solid #e5e7eb',
                        padding: '2px 8px', borderRadius: 10, fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <Form.Text>
                Normalização: <code>Médico Cardiologista</code> → <code>medico_cardiologista</code>
              </Form.Text>
            </>
          )}
        </Form.Group>
      </div>
      <div className="kb-modal__footer">
        <button type="button" className="kb-modal__btn kb-modal__btn--secondary" onClick={onHide}>
          Cancelar
        </button>
        <button
          type="button"
          className="kb-modal__btn kb-modal__btn--primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <><Spinner animation="border" size="sm" /> Salvando...</> : <><IconCheck size={16} /> Salvar</>}
        </button>
      </div>
    </Modal>
  );
};

// ===============================================
// KNOWLEDGE SOURCE CARD
// ===============================================

const KnowledgeSourceCard = ({ source, onDelete, onSync, onEditTags }) => {
  const Icon = getSourceIcon(source);
  const isProcessing = source.sync_status === 'processing';
  const colors = SOURCE_COLORS[source.source_type] || SOURCE_COLORS.file;

  const statusClass = source.is_processed
    ? 'kb-card__status--success'
    : isProcessing
    ? 'kb-card__status--warning'
    : 'kb-card__status--pending';

  const statusLabel = source.is_processed
    ? 'Ativo'
    : isProcessing
    ? 'Processando'
    : 'Pendente';

  const StatusIcon = source.is_processed ? IconCheck : isProcessing ? IconClock : null;

  return (
    <div className="kb-card">
      <div className="kb-card__left">
        <div
          className="kb-card__icon"
          style={{ background: colors.gradient, color: colors.color }}
        >
          <Icon size={18} />
        </div>
        <div className="kb-card__info">
          <div className="kb-card__header">
            <span className="kb-card__name">
              {source.file?.name || source.file_name || source.external_config?.file_name || source.external_url || source.name || 'Sem nome'}
            </span>
            <span className={`kb-card__status ${statusClass}`}>
              {StatusIcon && <StatusIcon size={10} />}
              {statusLabel}
            </span>
          </div>
          <div className="kb-card__meta">
            {source.file_size && (
              <span>{(source.file_size / 1024).toFixed(0)} KB</span>
            )}
            {source.chunk_count && (
              <span>{source.chunk_count} chunks</span>
            )}
            {source.last_sync_at && (
              <span>Sync: {source.last_sync_at}</span>
            )}
          </div>
          {/* Tags do documento (chips clicáveis pra editar) */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginTop: 6, flexWrap: 'wrap',
            }}
          >
            {(source.categories || []).map((cat) => (
              <span
                key={cat}
                style={{
                  background: '#e0f2fe', color: '#0369a1',
                  padding: '2px 8px', borderRadius: 10,
                  fontSize: 11, fontWeight: 500,
                }}
              >
                {cat}
              </span>
            ))}
            <button
              onClick={() => onEditTags(source)}
              style={{
                background: 'none', border: '1px dashed #cbd5e1',
                color: '#64748b', padding: '2px 8px', borderRadius: 10,
                fontSize: 11, cursor: 'pointer',
              }}
              title="Editar categorias"
            >
              {(source.categories || []).length === 0 ? '+ Tags' : 'Editar tags'}
            </button>
          </div>
          {isProcessing && (
            <div className="kb-card__progress">
              <div className="kb-card__progress-bar" />
            </div>
          )}
        </div>
      </div>
      <div className="kb-card__actions">
        {source.source_type !== 'file' && (
          <button
            className="kb-card__btn"
            onClick={() => onSync(source)}
            disabled={isProcessing}
            title="Sincronizar"
          >
            <IconRefresh size={15} />
          </button>
        )}
        <button
          className="kb-card__btn kb-card__btn--danger"
          onClick={() => onDelete(source)}
          title="Remover"
        >
          <IconTrash size={15} />
        </button>
      </div>
    </div>
  );
};

// ===============================================
// UPLOAD ZONE
// ===============================================

const UploadZone = ({ onUpload, uploading }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const file = files[0];

    if (file.size > UPLOAD_LIMITS.maxFileSize) {
      Swal.fire({
        icon: 'error',
        title: 'Arquivo muito grande',
        text: `O arquivo deve ter no máximo ${UPLOAD_LIMITS.maxFileSize / 1024 / 1024}MB.`,
      });
      return;
    }

    if (!UPLOAD_LIMITS.allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: 'error',
        title: 'Tipo de arquivo não suportado',
        text: `Tipos permitidos: ${UPLOAD_LIMITS.allowedExtensions.join(', ')}`,
      });
      return;
    }

    onUpload(file);
  };

  const classes = [
    'kb-upload',
    dragActive && 'kb-upload--active',
    uploading && 'kb-upload--disabled',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={UPLOAD_LIMITS.allowedExtensions.join(',')}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <IconUpload size={32} className="kb-upload__icon" />
      <p className="kb-upload__text">
        {uploading
          ? 'Enviando arquivo...'
          : 'Arraste arquivos aqui ou clique para selecionar'}
      </p>
      <small className="kb-upload__hint">
        PDF, DOCX, XLSX, CSV, TXT (máx. 1MB)
      </small>
    </div>
  );
};

// ===============================================
// WEBSITE MODAL
// ===============================================

const WebsiteModal = ({ show, onHide, onSave }) => {
  const [url, setUrl] = useState('');
  const [syncFrequency, setSyncFrequency] = useState('weekly');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedOptions, setAdvancedOptions] = useState({
    max_pages: 10,
    include_patterns: '',
    exclude_patterns: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    if (!url.trim().startsWith('https://')) {
      alert('Apenas URLs com HTTPS são aceitas.');
      return;
    }

    setLoading(true);
    try {
      await onSave({
        url,
        sync_frequency: syncFrequency,
        ...advancedOptions,
      });
      setUrl('');
      setAdvancedOptions({
        max_pages: 10,
        include_patterns: '',
        exclude_patterns: '',
      });
      onHide();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="kb-modal">
      <div className="kb-modal__header">
        <button className="kb-modal__close" onClick={onHide}>
          <IconX size={18} />
        </button>
        <div className="kb-modal__icon kb-modal__icon--website">
          <IconWorld size={28} />
        </div>
        <h2 className="kb-modal__title">Adicionar Website</h2>
        <p className="kb-modal__subtitle">
          O conteúdo da página será extraído e indexado automaticamente
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="kb-modal__body">
          <Form.Group className="mb-3">
            <Form.Label>URL do Website</Form.Label>
            <Form.Control
              type="url"
              pattern="https://.*"
              placeholder="https://seusite.com/pagina"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <Form.Text style={{fontWeight:500}}>Apenas sites com HTTPS são aceitos</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Frequência de Atualização</Form.Label>
            <Form.Select
              value={syncFrequency}
              onChange={(e) => setSyncFrequency(e.target.value)}
            >
              <option value="manual">Manual</option>
              <option value="daily">Diária</option>
              <option value="weekly">Semanal</option>
            </Form.Select>
          </Form.Group>

          <button
            type="button"
            className="kb-modal__advanced-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <IconSettings size={16} />
            {showAdvanced ? 'Ocultar opções avançadas' : 'Mostrar opções avançadas'}
          </button>

          {showAdvanced && (
            <div className="kb-modal__advanced">
              <div className="kb-modal__form">
                <Form.Group>
                  <Form.Label>Máximo de Páginas</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={25}
                    value={advancedOptions.max_pages}
                    onChange={(e) => setAdvancedOptions({
                      ...advancedOptions,
                      max_pages: Math.min(25, parseInt(e.target.value) || 1)
                    })}
                  />
                  <Form.Text>Limite de páginas a extrair (máx. 25)</Form.Text>
                </Form.Group>
                <div className="kb-modal__field--full">
                  <Form.Group>
                    <Form.Label>Padrões de Inclusão (URLs)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder={"/blog/\n/docs/"}
                      value={advancedOptions.include_patterns}
                      onChange={(e) => setAdvancedOptions({
                        ...advancedOptions,
                        include_patterns: e.target.value
                      })}
                    />
                    <Form.Text>Apenas URLs que contenham estes padrões (um por linha)</Form.Text>
                  </Form.Group>
                </div>
                <div className="kb-modal__field--full">
                  <Form.Group>
                    <Form.Label>Padrões de Exclusão (URLs)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder={"/admin/\n/login/"}
                      value={advancedOptions.exclude_patterns}
                      onChange={(e) => setAdvancedOptions({
                        ...advancedOptions,
                        exclude_patterns: e.target.value
                      })}
                    />
                    <Form.Text>Ignorar URLs que contenham estes padrões</Form.Text>
                  </Form.Group>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="kb-modal__footer">
          <div className="kb-modal__footer-info">
            <IconInfoCircle size={18} className="kb-modal__footer-info-icon" />
            <div>
              <strong>Como funciona?</strong>
              <p>
                Ao adicionar, o sistema irá ler automaticamente a página informada
                e navegar pelos links internos do site, extraindo todo o conteúdo
                relevante para treinar seu agente. Serão lidas até <strong>25 páginas</strong> por site.
              </p>
            </div>
          </div>
          <div className="kb-modal__footer-actions">
            <button type="button" className="kb-modal__btn kb-modal__btn--secondary" onClick={onHide}>
              Cancelar
            </button>
            <button type="submit" className="kb-modal__btn kb-modal__btn--primary" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Adicionando...
                </>
              ) : (
                <>
                  <IconPlus size={18} />
                  Adicionar
                </>
              )}
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

// ===============================================
// NOTION MODAL
// ===============================================

const NotionModal = ({ show, onHide, onSave }) => {
  const [config, setConfig] = useState({
    database_id: '',
    page_id: '',
    sync_frequency: 'daily',
    include_children: true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!config.database_id && !config.page_id) {
      Swal.fire({
        icon: 'error',
        title: 'Configuração necessária',
        text: 'Informe o ID do Database ou da Página do Notion.',
      });
      return;
    }

    setLoading(true);
    try {
      await onSave({
        source_type: 'notion',
        ...config,
      });
      setConfig({
        database_id: '',
        page_id: '',
        sync_frequency: 'daily',
        include_children: true,
      });
      onHide();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="kb-modal">
      <div className="kb-modal__header">
        <button className="kb-modal__close" onClick={onHide}>
          <IconX size={18} />
        </button>
        <div className="kb-modal__icon kb-modal__icon--notion">
          <IconBrandNotion size={28} />
        </div>
        <h2 className="kb-modal__title">Conectar Notion</h2>
        <p className="kb-modal__subtitle">
          Conecte uma página ou database do Notion para usar como base de conhecimento
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="kb-modal__body">
          <div className="kb-modal__info">
            <IconInfoCircle size={16} />
            Conecte uma página ou database do Notion para usar como base de conhecimento.
          </div>

          <Form.Group className="mb-3">
            <Form.Label>ID do Database (opcional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="abc123def456..."
              value={config.database_id}
              onChange={(e) => setConfig({ ...config, database_id: e.target.value })}
            />
            <Form.Text>
              Encontre o ID na URL do database: notion.so/abc123def456
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>ID da Página (opcional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="xyz789..."
              value={config.page_id}
              onChange={(e) => setConfig({ ...config, page_id: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Frequência de Sincronização</Form.Label>
            <Form.Select
              value={config.sync_frequency}
              onChange={(e) => setConfig({ ...config, sync_frequency: e.target.value })}
            >
              <option value="manual">Manual</option>
              <option value="hourly">A cada hora</option>
              <option value="daily">Diária</option>
              <option value="weekly">Semanal</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="switch"
              id="include-children"
              label="Incluir páginas filhas"
              checked={config.include_children}
              onChange={(e) => setConfig({ ...config, include_children: e.target.checked })}
            />
          </Form.Group>
        </div>

        <div className="kb-modal__footer">
          <button type="button" className="kb-modal__btn kb-modal__btn--secondary" onClick={onHide}>
            Cancelar
          </button>
          <button type="submit" className="kb-modal__btn kb-modal__btn--primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Conectando...
              </>
            ) : (
              <>
                <IconCheck size={18} />
                Conectar
              </>
            )}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

// ===============================================
// GOOGLE DRIVE MODAL
// ===============================================

const FILE_TYPE_ICONS = {
  'application/pdf': { Icon: IconFileTypePdf, color: '#dc3545' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { Icon: IconFileTypeDocx, color: '#2b579a' },
  'application/msword': { Icon: IconFileTypeDoc, color: '#2b579a' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { Icon: IconFileTypeXls, color: '#217346' },
  'application/vnd.ms-excel': { Icon: IconFileTypeXls, color: '#217346' },
  'text/csv': { Icon: IconFileTypeCsv, color: '#217346' },
  'text/plain': { Icon: IconFileTypeTxt, color: '#666' },
  'application/vnd.google-apps.document': { Icon: IconFileTypeDocx, color: '#4285f4' },
  'application/vnd.google-apps.spreadsheet': { Icon: IconFileTypeXls, color: '#0f9d58' },
};

const getFileTypeIcon = (mimeType) => {
  return FILE_TYPE_ICONS[mimeType] || { Icon: IconFile, color: '#888' };
};



const GoogleDriveModal = ({ show, onHide, onSave, agentId, integrationId, maxFiles }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [syncFrequency, setSyncFrequency] = useState('daily');
  const [loading, setLoading] = useState(false);

  const isConnectedMode = !!integrationId;

  // Google Picker hook
  const { openPicker } = useGooglePicker({
    agentId,
    integrationId,
    integrationType: 'google_drive',
    mimeTypes: DRIVE_RAG_MIME_TYPES,
    multiSelect: true,
    onSelect: (files) => {
      setSelectedFiles((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const newFiles = files.filter((f) => !existingIds.has(f.id));
        const merged = [...prev, ...newFiles];
        if (maxFiles != null && merged.length > maxFiles) {
          Swal.fire({ icon: 'warning', title: 'Limite atingido', text: `Máximo de ${maxFiles} arquivos por agente.` });
          return merged.slice(0, maxFiles);
        }
        return merged;
      });
    },
    onError: (msg) => {
      Swal.fire({ icon: 'error', title: 'Erro', text: msg });
    },
  });

  useEffect(() => {
    if (show) setSelectedFiles([]);
  }, [show]);

  // Submit
  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Nenhum arquivo selecionado', text: 'Selecione pelo menos um arquivo do Google Drive.' });
      return;
    }

    setLoading(true);
    try {
      await onSave({
        selected_files: selectedFiles.map((f) => ({
          id: f.id,
          name: f.name,
          mime_type: f.mime_type,
        })),
        sync_frequency: syncFrequency,
      });
      setSelectedFiles([]);
      onHide();
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  return (
    <Modal show={show} onHide={onHide} centered className="kb-modal" size={isConnectedMode ? 'lg' : undefined}>
      <div className="kb-modal__header">
        <button className="kb-modal__close" onClick={onHide}>
          <IconX size={18} />
        </button>
        <div className="kb-modal__icon kb-modal__icon--drive">
          <IconBrandGoogleDrive size={28} />
        </div>
        <h2 className="kb-modal__title">
          {isConnectedMode ? 'Selecionar Arquivos do Google Drive' : 'Conectar Google Drive'}
        </h2>
        <p className="kb-modal__subtitle">
          {isConnectedMode
            ? 'Use o seletor do Google para escolher os arquivos da base de conhecimento'
            : 'Conecte uma pasta do Google Drive para sincronizar documentos automaticamente'}
        </p>
      </div>

      {isConnectedMode ? (
        <div>
          <div className="kb-modal__body">
            {/* Botão para abrir Google Picker */}
            <button
              type="button"
              onClick={openPicker}
              style={{
                width: '100%', padding: '14px', border: '2px dashed #d1d5db', borderRadius: '10px',
                background: '#fafafa', cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px', fontSize: '14px', fontWeight: 500, color: '#4b5563',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ca8a04'; e.currentTarget.style.background = '#fffbeb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fafafa'; }}
            >
              <IconBrandGoogleDrive size={20} style={{ color: '#ca8a04' }} />
              Selecionar arquivos do Google Drive
            </button>

            {/* Lista de arquivos selecionados */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: 500 }}>
                  {selectedFiles.length} arquivo{selectedFiles.length !== 1 ? 's' : ''} selecionado{selectedFiles.length !== 1 ? 's' : ''}
                  {maxFiles != null && ` (máx. ${maxFiles})`}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedFiles.map((f) => {
                    const { Icon, color } = getFileTypeIcon(f.mime_type);
                    return (
                      <div key={f.id} style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
                        background: '#f9fafb', borderRadius: '6px', fontSize: '13px',
                      }}>
                        <Icon size={16} style={{ color, flexShrink: 0 }} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <button
                          onClick={() => removeFile(f.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}
                          title="Remover"
                        >
                          <IconX size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Form.Group className="mt-3">
              <Form.Label>Frequência de Sincronização</Form.Label>
              <Form.Select value={syncFrequency} onChange={(e) => setSyncFrequency(e.target.value)}>
                <option value="manual">Manual</option>
                <option value="hourly">A cada hora</option>
                <option value="daily">Diária</option>
                <option value="weekly">Semanal</option>
              </Form.Select>
            </Form.Group>
          </div>

          <div className="kb-modal__footer">
            <button type="button" className="kb-modal__btn kb-modal__btn--secondary" onClick={onHide}>
              Cancelar
            </button>
            <button
              type="button"
              className="kb-modal__btn kb-modal__btn--primary"
              disabled={loading || selectedFiles.length === 0}
              onClick={handleSubmit}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" /> Adicionando...</>
              ) : (
                <><IconPlus size={18} /> Adicionar {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}</>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* ========== NOT CONNECTED - REDIRECT TO INTEGRATIONS ========== */
        <div>
          <div className="kb-modal__body" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <IconAlertCircle size={28} style={{ color: '#ca8a04' }} />
            </div>
            <h5 style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
              Integração não configurada
            </h5>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: '0 auto 20px', maxWidth: 360 }}>
              Para buscar arquivos do Google Drive, primeiro acesse o módulo
              <strong> Integrações </strong> e autorize a integração com o Google Drive.
            </p>
            <button
              type="button"
              className="kb-modal__btn kb-modal__btn--primary"
              onClick={() => { onHide(); window.location.href = '/integrations'; }}
            >
              <IconSettings size={18} />
              Ir para Integrações
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ===============================================
// EMPTY STATE
// ===============================================

const EMPTY_STATES = {
  documents: {
    icon: IconFile,
    text: 'Nenhum documento adicionado.',
    subtitle: 'Envie PDFs, DOCX, planilhas ou arquivos de texto para o agente utilizar como referência.',
  },
  websites: {
    icon: IconWorld,
    text: 'Nenhum website adicionado.',
    subtitle: 'Adicione URLs para extrair conteúdo automaticamente.',
  },
  notion: {
    icon: IconBrandNotion,
    text: 'Nenhum Notion conectado.',
    subtitle: 'Conecte páginas ou databases do Notion para usar como base de conhecimento.',
  },
  drive: {
    icon: IconBrandGoogleDrive,
    text: 'Nenhum Google Drive conectado.',
    subtitle: 'Conecte pastas do Google Drive para sincronizar documentos automaticamente.',
  },
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const KnowledgeTab = ({ agentId }) => {
  const { state, updateKnowledge } = useAgentsFlow();
  const { uploadDocument, deleteKnowledge, syncKnowledge, addWebsiteKnowledge, loadKnowledge } = useAgentsFlowApi();
  const { editor } = state;
  const { knowledge } = editor;
  const { isProPlan } = useProFeature();

  const [activeFilter, setActiveFilter] = useState('documents');
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showNotionModal, setShowNotionModal] = useState(false);
  const [showDriveModal, setShowDriveModal] = useState(false);
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showRAGGuide, setShowRAGGuide] = useState(false);
  const [editTagsSource, setEditTagsSource] = useState(null);
  const [categorySuggestions, setCategorySuggestions] = useState([]);

  // Buscar tags existentes do agente para o autocomplete do modal de edição.
  // Recarrega quando knowledge muda (alguém adicionou/removeu tag).
  useEffect(() => {
    if (!agentId || agentId === 'novo') return;
    AgentsFlowService.getKnowledgeCategories(agentId)
      .then((resp) => setCategorySuggestions(resp.data || []))
      .catch(() => setCategorySuggestions([]));
  }, [agentId, knowledge.length]);

  // Filter knowledge by active filter types
  const currentFilterDef = FILTERS.find((f) => f.key === activeFilter);
  const filteredSources = knowledge.filter((k) =>
    currentFilterDef?.types.includes(k.source_type)
  );

  // Count per filter
  const getFilterCount = (filterKey) => {
    const def = FILTERS.find((f) => f.key === filterKey);
    return knowledge.filter((k) => def?.types.includes(k.source_type)).length;
  };

  const handleUpload = useCallback(async (file) => {
    const existingFileCount = knowledge.filter((k) => k.source_type === 'file').length;
    if (existingFileCount >= MAX_SOURCES_PER_TYPE) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite atingido',
        text: `Você pode adicionar no máximo ${MAX_SOURCES_PER_TYPE} documentos por agente.`,
      });
      return;
    }

    setUploading(true);

    const tempSource = {
      id: Date.now(),
      source_type: 'file',
      file: { name: file.name },
      file_size: file.size,
      file_type: file.type,
      is_processed: false,
      sync_status: 'processing',
    };
    updateKnowledge([...knowledge, tempSource]);

    try {
      if (agentId && agentId !== 'novo') {
        await uploadDocument(agentId, file);
      } else {
        setTimeout(() => {
          updateKnowledge(
            knowledge.map((k) =>
              k.id === tempSource.id ? { ...k, is_processed: true, sync_status: 'active' } : k
            )
          );
        }, 2000);
      }
    } catch (error) {
      updateKnowledge(knowledge.filter((k) => k.id !== tempSource.id));
      Swal.fire({
        icon: 'error',
        title: 'Erro ao enviar',
        text: 'Não foi possível enviar o documento.',
      });
    } finally {
      setUploading(false);
    }
  }, [knowledge, updateKnowledge, uploadDocument, agentId]);

  const handleDelete = useCallback(async (source) => {
    const result = await Swal.fire({
      title: 'Remover fonte?',
      text: 'O conteúdo não será mais usado pelo agente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      updateKnowledge(knowledge.filter((k) => k.id !== source.id));

      if (agentId && agentId !== 'novo') {
        try {
          await deleteKnowledge(agentId, source.id);
        } catch (error) {
          console.error('Error deleting knowledge:', error);
        }
      }
    }
  }, [knowledge, updateKnowledge, deleteKnowledge, agentId]);

  const handleSaveTags = useCallback(async (newTags) => {
    if (!editTagsSource) return;
    const updated = await AgentsFlowService.updateKnowledgeCategories(
      agentId,
      editTagsSource.id,
      newTags,
    );
    // Atualiza o item local com a resposta do servidor (categories normalizadas)
    const updatedData = updated.data || { ...editTagsSource, categories: newTags };
    updateKnowledge(
      knowledge.map((k) => (k.id === editTagsSource.id ? { ...k, ...updatedData } : k))
    );
    // Atualiza sugestões com a nova tag (se nova)
    setCategorySuggestions((prev) => {
      const merged = new Set([...prev, ...newTags]);
      return Array.from(merged).sort();
    });
  }, [editTagsSource, agentId, knowledge, updateKnowledge]);

  const handleSync = useCallback(async (source) => {
    updateKnowledge(
      knowledge.map((k) =>
        k.id === source.id ? { ...k, sync_status: 'processing' } : k
      )
    );

    if (agentId && agentId !== 'novo') {
      try {
        await syncKnowledge(agentId, source.id);
      } catch (error) {
        console.error('Error syncing knowledge:', error);
      }
    }
  }, [knowledge, updateKnowledge, syncKnowledge, agentId]);

  const handleAddWebsite = useCallback(async (data) => {
    const newSource = {
      id: Date.now(),
      source_type: 'website',
      external_url: data.url,
      sync_frequency: data.sync_frequency,
      is_processed: false,
      sync_status: 'pending',
    };
    updateKnowledge([...knowledge, newSource]);

    if (agentId && agentId !== 'novo') {
      try {
        await addWebsiteKnowledge(agentId, data);
      } catch (error) {
        updateKnowledge(knowledge.filter((k) => k.id !== newSource.id));
        throw error;
      }
    }
  }, [knowledge, updateKnowledge, addWebsiteKnowledge, agentId]);

  const handleAddNotion = useCallback(async (data) => {
    const newSource = {
      id: Date.now(),
      source_type: 'notion',
      name: data.database_id ? `Database: ${data.database_id.slice(0, 8)}...` : `Page: ${data.page_id.slice(0, 8)}...`,
      sync_frequency: data.sync_frequency,
      is_processed: false,
      sync_status: 'pending',
      ...data,
    };
    updateKnowledge([...knowledge, newSource]);

    Swal.fire({
      icon: 'success',
      title: 'Notion conectado!',
      text: 'A fonte será processada em breve.',
      timer: 1500,
      showConfirmButton: false,
    });
  }, [knowledge, updateKnowledge]);

  const handleAddDrive = useCallback(async (data) => {
    const existingDriveCount = knowledge.filter((k) => k.source_type === 'google_drive').length;
    const availableSlots = MAX_SOURCES_PER_TYPE - existingDriveCount;

    if (availableSlots <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite atingido',
        text: `Você pode adicionar no máximo ${MAX_SOURCES_PER_TYPE} arquivos do Google Drive por agente.`,
      });
      return;
    }

    // Connected mode: multiple files selected
    if (data.selected_files && data.selected_files.length > 0) {
      // Trim selection to available slots
      const filesToAdd = data.selected_files.slice(0, availableSlots);
      if (filesToAdd.length < data.selected_files.length) {
        Swal.fire({
          icon: 'info',
          title: 'Limite parcial',
          text: `Apenas ${filesToAdd.length} de ${data.selected_files.length} arquivos foram adicionados (limite de ${MAX_SOURCES_PER_TYPE} por agente).`,
        });
      }

      const newSources = filesToAdd.map((file, idx) => ({
        id: Date.now() + idx,
        source_type: 'google_drive',
        name: file.name,
        file_id: file.id,
        mime_type: file.mime_type,
        sync_frequency: data.sync_frequency,
        is_processed: false,
        sync_status: 'pending',
      }));
      updateKnowledge([...knowledge, ...newSources]);

      // Call API for each file if agent is saved
      if (agentId && agentId !== 'novo') {
        for (const file of filesToAdd) {
          try {
            await AgentsFlowService.connectGoogleDrive(agentId, {
              source_type: 'google_drive',
              sync_frequency: data.sync_frequency,
              external_config: {
                file_id: file.id,
                file_name: file.name,
                mime_type: file.mime_type,
              },
            });
          } catch (error) {
            console.error('Error connecting Drive file:', error);
          }
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Arquivos adicionados!',
        text: `${filesToAdd.length} arquivo${filesToAdd.length !== 1 ? 's' : ''} do Google Drive adicionado${filesToAdd.length !== 1 ? 's' : ''}.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } else {
      // Manual mode: folder_id
      const newSource = {
        id: Date.now(),
        source_type: 'google_drive',
        name: `Pasta: ${data.folder_id.slice(0, 8)}...`,
        sync_frequency: data.sync_frequency,
        is_processed: false,
        sync_status: 'pending',
        ...data,
      };
      updateKnowledge([...knowledge, newSource]);

      Swal.fire({
        icon: 'success',
        title: 'Google Drive conectado!',
        text: 'Os documentos serão sincronizados em breve.',
        timer: 1500,
        showConfirmButton: false,
      });
    }
  }, [knowledge, updateKnowledge, agentId]);

  // Stats
  const totalChunks = knowledge.reduce((acc, k) => acc + (k.chunk_count || 0), 0);
  const activeCount = knowledge.filter((k) => k.is_processed).length;
  const pendingCount = knowledge.filter((k) => !k.is_processed).length;

  // Action button per filter
  const handleFilterAction = () => {
    switch (activeFilter) {
      case 'websites':
        setShowWebsiteModal(true);
        break;
      case 'notion':
        setShowNotionModal(true);
        break;
      case 'drive':
        setShowDriveModal(true);
        break;
      default:
        break;
    }
  };

  const FILTER_ACTIONS = {
    websites: { label: 'Adicionar URL', icon: IconWorld },
    notion: { label: 'Conectar Notion', icon: IconBrandNotion },
    drive: { label: 'Conectar Google Drive', icon: IconBrandGoogleDrive },
  };

  const filterAction = FILTER_ACTIONS[activeFilter];
  const emptyState = EMPTY_STATES[activeFilter];

  return (
    <div className="kb-config">
      {/* Stats Bar */}
      <div className="kb-stats">
        <span className="kb-stats__item">
          <span className="kb-stats__value">{knowledge.length}</span> fontes
        </span>
        <span className="kb-stats__item">
          <span className="kb-stats__value">{totalChunks}</span> chunks indexados
        </span>
        <span className="kb-stats__badge">
          <IconCheck size={10} />
          {activeCount} ativas
        </span>
        <button
          className="kb-train-btn"
          onClick={() => setShowRAGGuide(true)}
          title="Guia Prático — Como estruturar sua base RAG"
        >
          <IconInfoCircle size={16} />
          Guia Prático
        </button>
        {knowledge.length > 0 && agentId && agentId !== 'novo' && (
          <button
            className={`kb-train-btn ${pendingCount > 0 ? 'kb-train-btn--highlight' : ''}`}
            onClick={() => setShowTrainModal(true)}
            title="Treinar Agente"
          >
            <IconBrain size={16} />
            Treinar
            {pendingCount > 0 && (
              <span className="kb-train-btn__badge">{pendingCount}</span>
            )}
          </button>
        )}
      </div>

      {/* Filter Pills */}
      <div className="kb-filters">
        {FILTERS.map((filter) => {
          const FilterIcon = filter.icon;
          const count = getFilterCount(filter.key);
          const isProFilter = filter.key === 'websites' && !isProPlan;
          return (
            <button
              key={filter.key}
              className={`kb-filter ${activeFilter === filter.key ? 'kb-filter--active' : ''}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              <FilterIcon size={14} />
              {filter.label}
              {isProFilter && <ProBadge small />}
              {count > 0 && (
                <span className="kb-filter__count">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Upload Zone (documents filter only) */}
      {activeFilter === 'documents' && (
        <UploadZone onUpload={handleUpload} uploading={uploading} />
      )}

      {/* Action Button (non-document filters) */}
      {filterAction && (
        <button className="kb-action-btn" onClick={handleFilterAction}>
          <filterAction.icon size={16} />
          {filterAction.label}
        </button>
      )}

      {/* Source List or Empty State */}
      {filteredSources.length > 0 ? (
        <div className="kb-list">
          {filteredSources.map((source) => (
            <KnowledgeSourceCard
              key={source.id}
              source={source}
              onDelete={handleDelete}
              onSync={handleSync}
              onEditTags={(s) => setEditTagsSource(s)}
            />
          ))}
        </div>
      ) : (
        emptyState && (
          <div className="kb-empty">
            <emptyState.icon size={40} className="kb-empty__icon" />
            <p className="kb-empty__text">{emptyState.text}</p>
            <p className="kb-empty__subtitle">{emptyState.subtitle}</p>
          </div>
        )
      )}

      {/* Modals */}
      <WebsiteModal
        show={showWebsiteModal}
        onHide={() => setShowWebsiteModal(false)}
        onSave={handleAddWebsite}
      />
      <NotionModal
        show={showNotionModal}
        onHide={() => setShowNotionModal(false)}
        onSave={handleAddNotion}
      />
      <GoogleDriveModal
        show={showDriveModal}
        onHide={() => setShowDriveModal(false)}
        onSave={handleAddDrive}
        agentId={agentId}
        integrationId={
          editor.integrations?.find(
            (i) => (i.integration_type === 'google_drive' || i.integration_type === 'google_workspace') && i.is_active
          )?.id
        }
        maxFiles={MAX_SOURCES_PER_TYPE - knowledge.filter((k) => k.source_type === 'google_drive').length}
      />
      <TrainAgentModal
        show={showTrainModal}
        onHide={() => setShowTrainModal(false)}
        agent={{ id: agentId, name: editor.identity?.name || 'Agente' }}
        onTrainingComplete={() => {
          setShowTrainModal(false);
          loadKnowledge(agentId);
        }}
      />
      <RAGGuideModal
        show={showRAGGuide}
        onHide={() => setShowRAGGuide(false)}
      />
      <EditTagsModal
        show={!!editTagsSource}
        source={editTagsSource}
        suggestions={categorySuggestions}
        onHide={() => setEditTagsSource(null)}
        onSave={handleSaveTags}
      />
    </div>
  );
};

export default KnowledgeTab;
